/* eslint-disable no-unused-vars, no-console */


import { register } from "register-service-worker";

// ---------------------------------------------------------------------------
// Update prompt.
//
// skipWaiting is deliberately NOT enabled in vue.config.js: a worker that
// takes over on its own would swap chunks under a radiologist who is part-way
// through a report. The cost of that decision is that a replacement worker
// sits in "waiting" for as long as ANY tab of the app is open, so a user can
// stay on a superseded build indefinitely — and every deploy deletes the old
// build's content-hashed files from static/, so that superseded build slowly
// falls apart as its images and lazy route chunks start returning 404.
//
// This closes the gap without taking the decision away from the user: when a
// new build is waiting, offer a reload and let them pick the moment. Nothing
// swaps until they click.
//
// Deliberately plain DOM with inline styles and no imports. It has to be able
// to render in a session whose app CSS and component chunks are exactly what
// went stale, so it cannot depend on element-ui, the theme, or any chunk that
// might itself fail to load.
// ---------------------------------------------------------------------------

const TOAST_ID = "qr-sw-update-toast";

// Guards the reload. controllerchange can fire more than once, and a reload
// loop on a medical app is worse than a stale tab.
let reloading = false;

function reloadOnce() {
  if (reloading) return;
  reloading = true;
  window.location.reload();
}

function applyUpdate(registration) {
  const waiting = registration && registration.waiting;

  // Nothing waiting means the new worker already took over; a plain reload is
  // enough to pick it up.
  if (!waiting || !navigator.serviceWorker) {
    reloadOnce();
    return;
  }

  // Reload once the replacement worker is actually in control, so the fresh
  // page is served by it rather than racing the handover.
  navigator.serviceWorker.addEventListener("controllerchange", reloadOnce);

  // workbox adds the matching listener to every generated service-worker.js.
  waiting.postMessage({ type: "SKIP_WAITING" });

  // Backstop: if the worker never reports back (it was already activating, or
  // the event is missed), reload anyway rather than leaving a dead button.
  setTimeout(reloadOnce, 3000);
}

function showUpdatePrompt(registration) {
  if (typeof document === "undefined" || document.getElementById(TOAST_ID)) return;

  const toast = document.createElement("div");
  toast.id = TOAST_ID;
  toast.setAttribute("role", "status");
  toast.style.cssText = [
    "position:fixed",
    "z-index:2147483647",
    "right:16px",
    "bottom:16px",
    "max-width:calc(100vw - 32px)",
    "display:flex",
    "align-items:center",
    "gap:12px",
    "padding:12px 14px",
    "border-radius:8px",
    "background:#1f2d3d",
    "color:#fff",
    "box-shadow:0 6px 24px rgba(0,0,0,.28)",
    "font:14px/1.4 Roboto,-apple-system,Segoe UI,sans-serif",
  ].join(";");

  const text = document.createElement("span");
  text.textContent = "A new version of QuickRad is available.";

  const reload = document.createElement("button");
  reload.type = "button";
  reload.textContent = "Reload";
  reload.style.cssText = [
    "flex:0 0 auto",
    "padding:6px 14px",
    "border:0",
    "border-radius:5px",
    "background:#409eff",
    "color:#fff",
    "font:600 14px/1 Roboto,-apple-system,Segoe UI,sans-serif",
    "cursor:pointer",
  ].join(";");
  reload.addEventListener("click", () => {
    reload.disabled = true;
    reload.textContent = "Reloading…";
    applyUpdate(registration);
  });

  // Dismiss is per page load, not permanent: the prompt returns on the next
  // load so a stale session cannot be dismissed forever by accident.
  const dismiss = document.createElement("button");
  dismiss.type = "button";
  dismiss.setAttribute("aria-label", "Dismiss");
  dismiss.textContent = "×";
  dismiss.style.cssText = [
    "flex:0 0 auto",
    "padding:0 4px",
    "border:0",
    "background:transparent",
    "color:#c0ccda",
    "font:400 20px/1 Roboto,-apple-system,Segoe UI,sans-serif",
    "cursor:pointer",
  ].join(";");
  dismiss.addEventListener("click", () => toast.remove());

  toast.appendChild(text);
  toast.appendChild(reload);
  toast.appendChild(dismiss);
  document.body.appendChild(toast);
}

if (process.env.NODE_ENV === "production") {
  register(`${process.env.BASE_URL}service-worker.js`, {
    ready() {

      //   "App is being served from cache by a service worker.\n" +
      //     "For more details, visit https://goo.gl/AFskqB"
      // );
    },
    registered() {

    },
    cached() {

    },
    updatefound() {

    },
    updated(registration) {
      // A new build finished downloading and is waiting. Offer it; do not
      // apply it behind the user's back.
      showUpdatePrompt(registration);
    },
    offline() {

      //   "No internet connection found. App is running in offline mode."
      // );
    },
    error(error) {
      console.error("Error during service worker registration:", error);
    },
  });
}
